import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CookieIcon, FileLockIcon, FlaskIcon, ShieldCheckIcon } from "@phosphor-icons/react";
import { AccountBar as DsAccountBar } from "@/zapi-frontend/widgets/Header/ui/blocks/AccountBar";
import type {
  OrganizationMenuAction,
  OrganizationMenuLabels
} from "@/zapi-frontend/widgets/Header/ui/blocks/OrganizationMenu";
import { RichMenuItem, RichMenuLabel, RichMenuSwitch } from "@/zapi-frontend/widgets/Header/ui/blocks/RichMenu";
import { DropdownMenuGroup } from "@libs/zapi-ui/ds/components/dropdown-menu";
import { useNotificationsMenu } from "@/zapi-frontend/features/Notifications";
import { useLabsModeToggle } from "@/zapi-frontend/features/LabsModeSwitch";
import { useSignOut } from "@/zapi-frontend/widgets/UserMenu";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import {
  getRouteCookieNotice,
  getRoutePrivacyNotice,
  getRouteSettings,
  getRouteTermsOfUse
} from "@/zapi-frontend/shared/const/router";
import { hasAdminPrivileges } from "@/zapi-frontend/shared/utils/permissions";
import type { TSettingsState } from "../lib/settingsReturn";

const settingsTabByAction: Record<Exclude<OrganizationMenuAction, "sign-out">, string> = {
  settings: "General",
  team: "Team",
  profile: "Profile"
};

export function AccountBar() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const signOut = useSignOut();
  const labsMode = useLabsModeToggle();
  const { role, email, organizationName, organizationLogoUrl } = useMeStore();
  const notificationsMenu = useNotificationsMenu();

  const runAction = (action: OrganizationMenuAction) => {
    if (action === "sign-out") return void signOut();
    const target = `${getRouteSettings()}?tab=${settingsTabByAction[action]}`;
    if (location.pathname === getRouteSettings()) return void navigate(target, { state: location.state });
    const state: TSettingsState = { settingsFrom: location.pathname + location.search };
    navigate(target, { state });
  };

  const organizationMenuLabels: OrganizationMenuLabels = {
    trigger: (name) => t("userMenu.menuLabel", { name }),
    organization: t("userMenu.organization"),
    settings: t("userMenu.organizationSettings"),
    settingsDescription: t("userMenu.organizationSettingsDescription"),
    team: t("userMenu.teamMembers"),
    teamDescription: t("userMenu.teamMembersDescription"),
    account: t("userMenu.account"),
    profile: t("userMenu.yourProfile"),
    signOut: t("userMenu.signOut")
  };

  return (
    <DsAccountBar
      organization={{ name: organizationName ?? "", logoUrl: organizationLogoUrl ?? undefined }}
      member={email ? { email } : undefined}
      notifications={notificationsMenu.notifications}
      notificationsLabels={notificationsMenu.labels}
      onMarkAllNotificationsRead={notificationsMenu.onMarkAllRead}
      onNotificationSelect={notificationsMenu.onSelect}
      showsOrganizationActions={hasAdminPrivileges(role)}
      organizationMenuLabels={organizationMenuLabels}
      onOrganizationAction={runAction}
      organizationMenuItems={
        <>
          {labsMode.available && (
            <RichMenuSwitch
              icon={FlaskIcon}
              title={t("labsMode.title")}
              description={t("labsMode.menuDescription")}
              checked={labsMode.enabled}
              onCheckedChange={(checked) => void labsMode.setEnabled(checked)}
              data-testid="labs-mode-switch"
            />
          )}
          <DropdownMenuGroup>
            <RichMenuLabel>{t("userMenu.legal")}</RichMenuLabel>
            <RichMenuItem
              icon={ShieldCheckIcon}
              title={t("userMenu.privacyNotice")}
              onClick={() => navigate(getRoutePrivacyNotice())}
            />
            <RichMenuItem
              icon={FileLockIcon}
              title={t("userMenu.termsOfUse")}
              onClick={() => navigate(getRouteTermsOfUse())}
            />
            <RichMenuItem
              icon={CookieIcon}
              title={t("userMenu.cookieNotice")}
              onClick={() => navigate(getRouteCookieNotice())}
            />
          </DropdownMenuGroup>
        </>
      }
    />
  );
}
